import * as _ from "lodash";
import * as moment from "moment-timezone";

import { getDay } from "src/core/orders/utils/order-deliveries";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import { totalPriceWithVAT } from "src/core/prices/utils/price-helpers";
import {
  IOrder,
  IOrderLine,
  IOrderStatus,
  OrderMode
} from "src/core/orders/api/orders.api";
import { IRole } from "src/core/shared/api/roles.api";
import {
  IList,
  IListLine
} from "src/core/lists/api/lists.api";
import {
  GroupedSettings,
  IMinimumOrderQuantitySetting,
  IMinimumOrderValueSetting,
  IDrop
} from "src/core/drops/api/drops.api";
import {
  IProduct,
  IProductRelatedGroup,
  IProductLeadTimeGroup,
  ProductStatus
} from "src/core/products/api/products.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import {
  ITerminology,
  roundOrToFixed
} from "src/library/utils";
import { IPackSizeConstraint } from "src/core/shared/store/global/global.reducer";

import {
  totalPriceVATOnly,
  totalPrice
} from "../../prices/utils/price-helpers";
import { packSizeConstraints } from "./pack-size-constraints";

export interface CalculatedTotal {
  net: number,
  vat: number;
  total: number
}

export const canMarkOrderAsManuallyProcessed = (order: IOrder, role: IRole, erp: string | null): string | null => {
  // If the user is not an admin
  if (role.type !== "admin") {
    return "Admin only action";
  }

  // If the ERP should not be able to perform this action
  if (erp === "seedless") {
    return "Re-sync not available without ERP connector";
  }

  // If the order status is not one of the allowedStatusTransitions
  if (order.orderStatus && !isAllowedStatusTransition(order.orderStatus, "manually_processed")) {
    return "This action is not available from the current order status";
  }

  return null;
};

/**
 * Check if a status transition is allowed.
 *
 * @param toStatus - the status which the order will be moved to.
 *
 * @returns - TRUE if the status transition is allowed.
 */
export const isAllowedStatusTransition = (currentStatus: IOrderStatus, newStatusCode: string): boolean => {
  // allow no change to status
  if (newStatusCode === currentStatus.code) {
    return true;
  }

  if (isFinalStatus(currentStatus)) {
    return false;
  }

  // if settings includes status then allow
  return currentStatus.settings.allowedTransitions.includes(newStatusCode);
};

/**
 * Check if the order has a status that can't be changed:
 *
 * - Cancelled.
 * - Dispatched.
 *
 * @returns - TRUE if the order has a final status.
 */
export const isFinalStatus = (currentStatus: IOrderStatus): boolean => {
  return currentStatus.settings.allowedTransitions.length === 0;
};

/**
 * Check if the order is locked by its order settings status
 *
 * @returns - TRUE if the order is locked
 */
export const orderIsLockedByStatus = (currentStatus: IOrderStatus): boolean => {
  return currentStatus.settings.lockOrders;
};

/**
 * Check to see if you can resync an order from the ERP
 *
 * @returns - TRUE if the order has a final status.
 */
export const canResyncOrder = (order: IOrder, role: IRole, erp: string | null): string | null => {
  if (role.type !== "admin") {
    return "Admin only action";
  }

  if (erp === "seedless") {
    return "Re-sync not available without ERP connector";
  }

  if (!order.orderReference) {
    return `Cannot sync an order without valid ${erp} order reference;`;
  }

  return null;
};

/**
 * Check if the delivery date of an order has passed.
 *
 * @param {IOrder} order - order object.
 *
 * @return - Whether the date has passed or not.
 */
export const deliveryDateHasPassed = (deliverydate: Date): boolean => {
  const now = moment.utc();
  const deliveryDate = moment.utc(deliverydate);

  return now.isAfter(deliveryDate, "day");
};

/**
 * Check if the minimum order value has been met.
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return - Whether the value was met or not.
 */
export const orderMinimumValueNotMet = <T extends IOrder | IList>(orderOrList: T, minOrderValue: IMinimumOrderValueSetting): boolean => {
  const totalValue = calculateCartTotal((orderOrList as IOrder).orderLines || (orderOrList as IList).listLines);

  return totalValue.total < minOrderValue.value;
};

/**
 * Check if the minimum order quantity has been met.
 *
 * @param {T} orderOrList - order or list object.
 * @param {IMinimumOrderQuantitySetting} minOrderQuantitySetting - settings for min order qty.
 * @param {ITerminology} terminology - terinology used for list and order words.
 *
 * @return - Null returned if the quantity is met.
 */
export const orderMinimumQuantityMet = <T extends IOrder | IList>(orderOrList: T, minOrderQuantitySetting: IMinimumOrderQuantitySetting, terminology?: ITerminology): string | null => {
  const orderOrListLines = (orderOrList as IOrder).orderLines || (orderOrList as IList).listLines;
  const totalOrderQty = _.sumBy(orderOrListLines, order => order.quantity);
  const minOrderQtySetting: { [key: string]: number } = { ...minOrderQuantitySetting };
  const minOrderQuantityValue = minOrderQtySetting.quantity;
  const isList = !!(orderOrList as IList).listLines;

  // Remove the quantity from the object as this variable is for checking all other minimum order quantities
  delete minOrderQtySetting.quantity;

  // Run packsize checks if there are more minimum order quantity settings provided
  if (Object.keys(minOrderQtySetting).length) {
    // Transform the setting to match shape for packSizeConstraints
    const minQtyConstraintsTransformed = _.map(minOrderQtySetting, (minQuantity, code) => ({
      code,
      minQuantity
    }));

    // Generate constraint variables for each pack size found in settings
    const minConstraints = packSizeConstraints(minQtyConstraintsTransformed, orderOrListLines);

    if (minConstraints.length) {
      // Loop and check for any constrain violations, returing violation if found
      for (const constraint of minConstraints) {
        if (constraint.minQuantity && constraint.quantity < constraint.minQuantity) {
          const inOrderOrListText = isList ? terminology?.list.singular.text || "list" : terminology?.order.singular.text || "order";

          return `
            The ${inOrderOrListText} must contain at least ${constraint.minQuantity} ${constraint.plural}. 
            Currently there are ${constraint.quantity.toFixed(2)}/${constraint.minQuantity} ${constraint.plural}
          `;
        }
      }

      return null;
    }
  }

  const quantityErrorMessage = isList ?
    `Standing orders cannot be submitted, as the minimum ${terminology?.list.singular.text || "list"} quantity of ${minOrderQuantityValue} was not met` :
    `Order cannot be submitted, as the minimum ${terminology?.order.singular.text || "order"} quantity of ${minOrderQuantityValue} was not met`;

  return totalOrderQty < minOrderQuantityValue ? quantityErrorMessage : null;
};

/**
 * Calculates total weight of a given order or list
 * @param orderOrList
 * @returns
 */
export const totalWeight = <T extends IOrder | IList | null>(orderOrList: T, formatted = true): number | null => {
  if (!orderOrList) {
    return null;
  }

  const orderOrListLines = (orderOrList as IOrder).orderLines || (orderOrList as IList).listLines;
  const totalWeight = _.sumBy(orderOrListLines, line => (line.product?.weight || 0) * line.quantity);

  return totalWeight === 0 ? null : formatted ? roundOrToFixed(totalWeight) : totalWeight;
};

/**
 * Checks order or list lines against max pack size constraints.
 * If weight included, total weight is calculated and presented as "quantity" in the transformed constraint object.
 *
 * @param {T} orderOrList - order or list object.
 * @param {IPackSizeConstraint[]} packSizeConstraints - packsize setting.
 * @param {ITerminology} terminology - terinology used for list and order words.
 * @param {string} errorLevel - defaults to "error" this allows for different error level validation
 * @return - Null returned if no violation found.
 */
export const validateAgainstMaxPackSizeConstraints = <T extends IOrder | IList>(orderOrList: T, packSizeConstraintSettings?: IPackSizeConstraint[], terminology?: ITerminology, errorLevel = "error"): string | null => {
  if (!packSizeConstraintSettings?.length) {
    return null;
  }

  const orderOrListLines = (orderOrList as IOrder).orderLines || (orderOrList as IList).listLines;
  const orderPackSizeConstraints = packSizeConstraints(packSizeConstraintSettings, orderOrListLines);
  const isList = !!(orderOrList as IList).listLines;
  const orderOrListTerminology = isList ? terminology?.list.singular.text || "list" : terminology?.order.singular.text || "order";

  if (orderPackSizeConstraints.length) {
    for (const constraint of orderPackSizeConstraints) {
      if (constraint.maxQuantity && constraint.quantity > constraint.maxQuantity && constraint.errorLevel === errorLevel) {
        let message = "";

        if (constraint.code === "weight") {
          message = `The ${orderOrListTerminology} weight is ${roundOrToFixed(constraint.quantity)}kg, exceeding max weight constraint of ${constraint.maxQuantity}kg.`;
        } else if (constraint.code === "pallet") {
          message = `The ${orderOrListTerminology} contains ${roundOrToFixed(constraint.quantity)} ${constraint.plural}, max quantity is ${constraint.maxQuantity}.`;
        } else {
          //add any more pack size constraint logic here if needed in the future
          return null;
        }

        //Additional message to show that the order can still be placed
        if (errorLevel === "warn") {
          message += " This order may be changed by an admin after being placed.";
        }

        return message;
      }
    }
  }

  return null;
};

export const getOrderLinesByDay = (order: IOrder, leadTimeGroups: IProductLeadTimeGroup[]): { [key: string]: IOrderLine[] } => {
  // make sure the lines start sorted by lead time group days
  const orderLinesSortedByLeadTime = _.map(order.orderLines, line => {
    const leadTimeGroup = _.find(leadTimeGroups, ltg => ltg.id === line.product.leadTimeGroupId);

    line.product.leadTimeGroup = leadTimeGroup;

    return line;
  }).sort((a, b) => {
    if ((a.product.leadTimeGroup?.defaultLeadTimeDays ?? 0) < (b.product.leadTimeGroup?.defaultLeadTimeDays ?? 0)) {
      return -1;
    }

    return 1;
  });

  const sortedOrderLines: { [key: string]: IOrderLine[] } = {};

  for (const line of orderLinesSortedByLeadTime) {
    const leadTimeGroup = _.find(leadTimeGroups, ltg => ltg.id === line.product.leadTimeGroupId);
    const groupKey = leadTimeGroup?.customerDescription || `${leadTimeGroup?.defaultLeadTimeDays} day lead time`;

    // Normal lead time splits
    if (!sortedOrderLines[ groupKey ]) {
      sortedOrderLines[ groupKey ] = [ line ];
    } else {
      sortedOrderLines[ groupKey ].push(line);
    }
  }

  // sort by partNumber
  for (const sortedOrderLine in sortedOrderLines) {
    sortedOrderLines[ sortedOrderLine ] = _.sortBy(sortedOrderLines[ sortedOrderLine ], orderLine => orderLine.product.partNumber);
  }

  return sortedOrderLines;
};

// string = relatedGroupParentId
export interface OrderLinesGroupedByRelatedGroupParent {
  [key: string]: {
    parentOrderLines: IOrderLine[];
    childOrderLines: IOrderLine[];
  },
}

/**
 * Reorders an array of order lines sorted by related group.
 * Parents and their relevant children are grouped together.
 * Any children order lines that have no parent.
 * Any order lines that are not part of a related group.
 *
 * @param orderLines
 * @param relatedGroups
 * @returns
 */
export const getOrderLinesByRelatedGroup = (orderLines: Array<IOrderLine | IListLine>, relatedGroups: IProductRelatedGroup[]): {
  orderLinesGroupedByParent: OrderLinesGroupedByRelatedGroupParent;
  childrenWithNoParent?: IOrderLine[];
  genericOrderLines?: IOrderLine[];
} => {
  const orderLinesAssociatedWithRelatedGroups = orderLines.filter(orderLine => orderLine.product.relatedGroupId);

  if (!orderLinesAssociatedWithRelatedGroups.length) {
    // Return early as no need to sort
    return {
      genericOrderLines: orderLines,
      orderLinesGroupedByParent: {}
    };
  }

  const orderLinesByRelatedGroup: IOrderLine[] = [];
  // To avoid setting duplicates, we loop through each orderline.
  // If the parent didn't exist at the time we processed the child, we make a note to come back to the child here.
  const remainingChildrenToProcess: IOrderLine[] = [];
  const childrenWithNoParent: IOrderLine[] = [];
  const genericOrderLines: IOrderLine[] = [];
  const orderLinesGroupedByParent: OrderLinesGroupedByRelatedGroupParent = {};

  // Divi up orderlines based on relatedGroup
  for (const orderLine of orderLines) {
    const relatedGroupId = orderLine.product.relatedGroupId || null;
    const relatedGroup = _.find(relatedGroups, prg => relatedGroupId === prg.id);

    if (!relatedGroupId || !relatedGroup) {
      // Add to generic order lines as no association to a related group
      genericOrderLines.push(orderLine);
      continue;
    }

    const parentKey = !relatedGroup.parentId ?
      // If no parentId on relatedGroup - use the relatedGroupId as the key
      relatedGroupId.toString() :
      // If parentId on relatedGroup - use the parentId as the key
      relatedGroup.parentId.toString();

    // Handle if order line product is a related group parent
    if (!relatedGroup.parentId) {
      // Its a parent if no parentId is set
      if (orderLinesGroupedByParent[ parentKey ]) {
        orderLinesGroupedByParent[ parentKey ].parentOrderLines.push(orderLine);
      } else {
        orderLinesGroupedByParent[ parentKey ] = {
          parentOrderLines: [ orderLine ],
          childOrderLines: []
        };
      }

      continue;
    }

    // If we get this far, the order line is a child.
    if (orderLinesGroupedByParent[ parentKey ]) {
      orderLinesGroupedByParent[ parentKey ].childOrderLines.push(orderLine);
    } else {
      // If the parent doesn't exist yet, we need to come back to this child later
      remainingChildrenToProcess.push(orderLine);
    }
  }

  // Process the remaining children that didn't have a parent
  for (const orderLine of remainingChildrenToProcess) {
    const relatedGroupId = orderLine.product.relatedGroupId || null;
    const relatedGroup = _.find(relatedGroups, prg => relatedGroupId === prg.id);

    if (!relatedGroupId || !relatedGroup) {
      genericOrderLines.push(orderLine);
      continue;
    }

    const parentKey = relatedGroup.parentId?.toString() || null;

    if (parentKey && orderLinesGroupedByParent[ parentKey ]) {
      // If parent exists, add to child order lines
      orderLinesGroupedByParent[ parentKey ].childOrderLines.push(orderLine);
    } else {
      childrenWithNoParent.push(orderLine);
    }
  }

  // Generate the final array of order lines
  for (const parentKey in orderLinesGroupedByParent) {
    const parentOrderLines = orderLinesGroupedByParent[ parentKey ].parentOrderLines;
    const childOrderLines = orderLinesGroupedByParent[ parentKey ].childOrderLines;

    // Add parent order lines
    orderLinesByRelatedGroup.push(...parentOrderLines);
    // Add child order lines
    orderLinesByRelatedGroup.push(...childOrderLines);
  }

  return {
    orderLinesGroupedByParent,
    childrenWithNoParent,
    genericOrderLines
  };
};

/**
 * Whether the order should show order line errors.
 *
 * Show orderline errors if:
 * Order is draft, or order has errors AND order is before cutoff
 * OR: order is being edited.
 *
 * @param order - The full order object
 * @param orderMode - OrderMode e.g. edit
 * @param settings - the drop's grouped settings
 *
 * @return {boolean} - true to show errors
 */
export const shouldShowOrderLineErrors = (order: IOrder, orderMode: OrderMode, settings: GroupedSettings): boolean => {
  if (settings.delivery_week_leadtimes && order.orderStatus && settings.delivery_cutoff) {
    if (orderMode === "edit") {
      return true;
    }
  }

  return false;
};

export const canListOrderProduct = (product: IProduct, schedule?: ISchedule | null): string | null => {
  if (product.madeToOrder && schedule) {
    return "Made to order products cannot be added to standing orders";
  }

  const productCurrentStatus = product?.currentStatus?.status;
  const productFutureStatus = product.productStatuses?.filter(status => moment.utc(status.startDate, "YYYY-MM-DD").isAfter(moment.utc()));
  const futureActiveStatus = productFutureStatus?.find(futureStatus => futureStatus.status === ProductStatus.Active);

  if (!productCurrentStatus) {
    return "Product not available";
  }

  // if product suspended, delisted or unpublished with no future active statuses
  if (productCurrentStatus === ProductStatus.Delisted && !futureActiveStatus) {
    return `Product ${productCurrentStatus}`;
  }

  if (!product.published) {
    return "Product not available";
  }

  if (!product.prices || product.prices.length === 0) {
    return "Product not available";
  }

  if (schedule && schedule.scheduleDays) {
    if (product.deliveryRoute && product.deliveryRoute[ 0 ]) {
      for (const scheduleDay of schedule.scheduleDays) {
        const deliveryDay = getDay(scheduleDay);

        if (deliveryDay) {
          if (!product.deliveryRoute[ 0 ].deliveryDays[ deliveryDay ]) {
            return "Product not available for selected delivery days";
          }
        }
      }
    }
  }

  return null;
};

/**
 * Calculates the price from a given drop setting
 * @param items
 * @param drop
 * @param discountTypes
 * @returns
 */
export const calculatePriceTotals = (
  items: IOrderLine[],
  drop?: IDrop | null,
): CalculatedTotal => {
  // apply discount to totals if exists
  if (drop?.discount) {
    return calculateCartTotal(items, drop.discount).discounted ?? calculateCartTotal(items);
  }

  return calculateCartTotal(items);
};

/**
 * Calculated the cart total including discounted values if discount value provided
 * @param items
 * @param discountType
 * @param discountValue
 * @returns
 */
export const calculateCartTotal = (
  items: IOrderLine[],
  discountValue?: number,
): CalculatedTotal & { discounted?: CalculatedTotal } => {
  let net = 0;
  let vat = 0;

  const beforeDiscount: CalculatedTotal = {
    total: 0,
    net: 0,
    vat: 0
  };

  const total = items.length ?
    items.reduce((tally: number, cartItem) => {
      // check for substitution
      const activeCartItem = (cartItem.substituteOrderLine || cartItem as IOrderLine);

      if (activeCartItem.substituteOrderLineId) {
        return tally;
      }

      if (activeCartItem.totalVatValue) {
        vat += (totalPriceVATOnly(activeCartItem) || 0);
      }

      net += totalPrice(activeCartItem);

      if (activeCartItem.totalPrice) {
        return tally + totalPriceWithVAT(activeCartItem);
      }

      return tally;
    }, 0) : 0;

  // handle applying invoice value level discount
  if (discountValue) {
    beforeDiscount.total = total + ((total * discountValue) / 100);

    if (vat) {
      beforeDiscount.vat = vat + ((vat * discountValue) / 100);
    }

    if (net) {
      beforeDiscount.net = net + ((net * discountValue) / 100);
    }
  } else {
    // return the original calculated totals against discounted,
    // this includes values already discounted which may match the given item lines above
    beforeDiscount.total = total;
    beforeDiscount.vat = vat;
    beforeDiscount.net = net;
  }

  if (beforeDiscount) {
    return {
      ...beforeDiscount,
      discounted: {
        total,
        vat,
        net
      }
    };
  }

  return {
    total,
    vat,
    net
  };
};

export const calculateCartTotalQuantity = (items: IOrderLine[]): number => {
  return items.length ?
    items.reduce((total: number, cartItem) => {
      // check for substitution
      const activeCartItem = cartItem.substituteOrderLine || cartItem;

      if (!activeCartItem.quantity) {
        return total;
      }

      return total + activeCartItem.quantity;
    }, 0) :
    0;
};

export const orderPendingPlaced = (order: IOrder, orderStatuses: IOrderStatus[]): boolean => {
  const provisionalId = getOrderStatusIdFromCode(orderStatuses, "provisional");
  // if no delivery date its made to order,

  if (!order.orderDeliveryDate) {
    return false;
  }

  // submited order status for erp means submitted but not placed
  if (order.orderStatusId === provisionalId) {
    return true;
  }

  return false;
};

/**
 * Check if an order or list includes a product below minimum order quantity.
 * If true, returns a string noting which product fails and the quantity required for said product
 * @param orderOrListLines
 */
export const productMinimumQuantityNotMet = <T extends IOrderLine[] | IListLine[]>(orderOrListLines: T): string | false => {
  if (orderOrListLines.length === 0) {
    return false;
  }

  for (let i = 0; i < orderOrListLines.length; ++i) {
    const currentProduct = orderOrListLines[ i ].product;

    if (!currentProduct.minimumQuantity) {
      continue;
    }

    if (orderOrListLines[ i ].quantity < currentProduct.minimumQuantity) {
      return `${currentProduct.description}, ${currentProduct.partNumber}, has a minimum order requirement of ${currentProduct.minimumQuantity}`;
    }
  }

  return false;
};

/**
 * Checks the PO number conforms to the settings
 *
 * @param value
 * @param drop
 * @returns
 */
export const poNumberIsValid = (value: string, drop: IDrop): string | null => {
  const poRequirements = drop?.groupedSettings.order_data_requirements?.value.orderPONumber;
  let maxLength = 20;

  if (typeof poRequirements === "object" && poRequirements.regex) {
    if (poRequirements.required && (!value || !value.length)) {
      return "PO numbr is required";
    }

    if (!new RegExp(poRequirements.regex.slice(1, -1)).test(value)) {
      return poRequirements.message;
    }

    if (poRequirements.maxLength) {
      maxLength = poRequirements.maxLength;
    }
  }

  if (value.length > maxLength) {
    return `PO numbers have a maximum of ${maxLength} characters`;
  } else {
    return null;
  }
};
